import { Injectable } from '@angular/core';
import { OptionService, OrderItemDTO } from 'ticket-sdk';
import { Storage } from '@ionic/storage';
import { BehaviorSubject, firstValueFrom, from } from 'rxjs';
import { first, map, switchMap, take } from 'rxjs/operators';
import { SimpleOrderItem } from '@app/shared/interfaces/simple-order-item.interface';
import { AlertController } from '@ionic/angular';

@Injectable({
  providedIn: 'root'
})
export class CheckoutService {
  minutesToExpireAfter: number = 120;
  private lastOrderUpdate: Date = null;
  private simpleOrderItems: BehaviorSubject<SimpleOrderItem[]> = new BehaviorSubject(null);

  constructor(
    private storage: Storage, 
    private alertController: AlertController,
    private optionService: OptionService
  ) {
    from(this.storage.ready()).pipe(
      take(1),
      switchMap(() => from(this.storage.get('order-items')).pipe(map(orderItems => {
        this.simpleOrderItems.next(!!orderItems ? orderItems : []);
      })))
    ).subscribe();

    from(this.storage.ready()).pipe(
      take(1),
      switchMap(() => from(this.storage.get('last-order-update')).pipe(map(lastOrderUpdate => {
        this.lastOrderUpdate = lastOrderUpdate;
      })))
    ).subscribe();
  }

  addOrderItem(orderItem: SimpleOrderItem): number {
    let orderItems: SimpleOrderItem[] = this.simpleOrderItems.value;
    this.setLastOrderUpdate(new Date());

    if (!!orderItems) {
      orderItems.push(orderItem);
      this.simpleOrderItems.next(orderItems);
      this.storage.set('order-items', orderItems);
  
      return orderItems.length;
    }
    else {
      return 0;
    }
  }

  calculateTotalCost(orderItems: SimpleOrderItem[]): number {
    return orderItems.map(orderItem => orderItem.price || orderItem.ticketPrice.price).reduce((a, b) => a + b);
  }

  checkIfOrderItemsExpired() {
    if (!!this.lastOrderUpdate) {
      let now: Date = new Date();
      if (new Date(now.getTime() - (this.minutesToExpireAfter * 60000)) > this.lastOrderUpdate ) {
        this.clearOrderItems();
      }
    }
  }

  clearOrderItems(): void {
    this.simpleOrderItems.next([]);
    this.storage.remove('order-items');

    this.resetLastOrderUpdate();
  }

  getSimpleOrderItems(): BehaviorSubject<SimpleOrderItem[]> {
    return this.simpleOrderItems;
  }

  getOrderItems(): OrderItemDTO[] {
    return this.simpleOrderItems.value.map(simpleOrderItem => {
      return {
          orderItemId: null,
          vehicleType: simpleOrderItem.ticketPrice.vehicleType,
          ticketType: simpleOrderItem.ticketPrice.ticketType,
          licensePlateNumber: simpleOrderItem.licensePlateNumber,
          originalQuantity: simpleOrderItem.ticketPrice.quantity,
          price: simpleOrderItem.price || simpleOrderItem.ticketPrice.price,
          vat: simpleOrderItem.ticketPrice.vehicleType.vat
        }
    });
  }

  removeOrderItem(index: number): number {
    let simpleOrderItems: SimpleOrderItem[] = this.simpleOrderItems.value;
    this.setLastOrderUpdate(new Date());

    if (!!simpleOrderItems) {
      simpleOrderItems.splice(index, 1);
      this.simpleOrderItems.next(simpleOrderItems);
      this.storage.set('order-items', simpleOrderItems);
  
      return simpleOrderItems.length;
    }
    else {
      return 0;
    }
  }

  resetLastOrderUpdate() {
    this.storage.remove('last-order-update');
    this.lastOrderUpdate = null;
  }

  setOrderItems(orderItems: SimpleOrderItem[]): void {
    this.simpleOrderItems.next([...orderItems]);
    this.storage.set('order-items', orderItems);

    this.setLastOrderUpdate(new Date());
  }

  setLastOrderUpdate(date: Date) {
    this.lastOrderUpdate = date;
    this.storage.set('last-order-update', date);
  }

  async confirmCheckoutIfNeeded(): Promise<boolean> {
    try {
      const freeModeActiveOption = await firstValueFrom(this.optionService.getFreeModeActiveOption());

      if (freeModeActiveOption.value.toLowerCase() === "true") {
        return this.showConfirmationAlert();
      }
      else {
        return true;
      }
    } catch (error) {
      console.error('Error in confirmCheckoutIfNeeded:', error);
      return false;
    }
  }

  private async showConfirmationAlert(): Promise<boolean> {
    const alert = await this.alertController.create({
      header: 'Bekräfta köp',
      message: 'Just nu är det avgiftsfritt att åka med Ekeröleden. Vill du ändå köpa biljett?',
      cssClass: 'warning-alert',
      buttons: [
        {
          text: 'Nej, AVBRYT',
          role: 'cancel',
          handler: () => false
        },
        {
          text: 'JA',
          handler: () => true
        }
      ]
    });

    await alert.present();
    const result = await alert.onDidDismiss();
    return result.role !== 'cancel' && result.role !== 'backdrop';
  }
}
